import { useEffect, useLayoutEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { ReactLenis, useLenis } from 'lenis/react';
import { AuthProvider } from './contexts/AuthContext';
import Navigation from './components/layout/Navigation';
import Footer from './components/layout/Footer';
import Hero from './components/sections/Hero';
import Gallery from './components/sections/Gallery';
import LocalGuide from './components/sections/LocalGuide';
import Contact from './components/sections/Contact';
import BookingEngine from './components/sections/BookingEngine';
import Login from './components/admin/Login';
import Dashboard from './components/admin/Dashboard';
import ProtectedRoute from './components/admin/ProtectedRoute';

function RouteScrollReset() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positionsByKey = useRef(new Map<string, number>());
  const positionsByUrl = useRef(new Map<string, number>());
  const activeLocation = useRef({ key: location.key, href: window.location.href });
  const lenis = useLenis((instance) => {
    positionsByKey.current.set(activeLocation.current.key, instance.scroll);
    positionsByUrl.current.set(activeLocation.current.href, instance.scroll);
  });

  useLayoutEffect(() => {
    const previous = activeLocation.current;
    const current = { key: location.key, href: window.location.href };

    if (previous.key !== current.key) {
      if (navigationType === 'POP') {
        lenis?.scrollTo(positionsByKey.current.get(current.key) ?? 0, { immediate: true });
      } else {
        lenis?.scrollTo(0, { immediate: true });
      }
    }

    activeLocation.current = current;
  }, [location.key, navigationType, lenis]);

  useEffect(() => {
    const restoreHashPosition = () => {
      const href = window.location.href;
      const savedPosition = positionsByUrl.current.get(href);

      if (savedPosition !== undefined) {
        lenis?.scrollTo(savedPosition, { immediate: true });
      } else if (window.location.hash) {
        lenis?.scrollTo(decodeURIComponent(window.location.hash), { immediate: true });
      } else {
        lenis?.scrollTo(0, { immediate: true });
      }

      activeLocation.current = { ...activeLocation.current, href };
    };

    window.addEventListener('hashchange', restoreHashPosition);
    return () => window.removeEventListener('hashchange', restoreHashPosition);
  }, [lenis]);

  return null;
}

function HouseDetails() {
  return (
    <section aria-label="House details" className="border-y border-gray-200 bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-gray-200 px-5 sm:grid-cols-3 sm:divide-x sm:divide-y-0 sm:px-8">
        <div className="py-5 sm:px-6">
          <p className="text-xs font-semibold uppercase text-gray-500">Capacity</p>
          <p className="mt-1 font-serif text-lg text-gray-900">Sleeps 4 · 2 Bedrooms</p>
        </div>
        <div className="py-5 sm:px-6">
          <p className="text-xs font-semibold uppercase text-gray-500">Comfort</p>
          <p className="mt-1 font-serif text-lg text-gray-900">Air Conditioning · Fast Wi-Fi · Full Kitchen</p>
        </div>
        <div className="py-5 sm:px-6">
          <p className="text-xs font-semibold uppercase text-gray-500">Booking</p>
          <p className="mt-1 font-serif text-lg text-gray-900">Instant Confirmation on Payment</p>
        </div>
      </div>
    </section>
  );
}

// The single long scrolling page: Home + Gallery + Contact + Book
function HomePage() {
  return (
    <div className="flex flex-col min-h-screen">
      <Navigation />
      <main className="flex-grow">
        <div id="home"><Hero /></div>
        <HouseDetails />
        <Gallery />
        <LocalGuide />
        <Contact />
        <section id="book"><BookingEngine /></section>
      </main>
      <Footer />
    </div>
  );
}

// Admin screens stay as separate pages (no scroll needed there)
function AdminPages() {
  return (
    <>
      <Navigation />
      <main className="flex-grow">
        <Routes>
          <Route path="login" element={<Login />} />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          {/* Booking management table (admin view of BookingEngine) */}
          <Route
            path="bookings"
            element={
              <ProtectedRoute>
                <BookingEngine isAdminView={true} />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <ReactLenis
      root
      options={{
        autoRaf: true,
        lerp: 0.1,
        smoothWheel: true,
        syncTouch: false,
        respectReducedMotion: true,
        stopInertiaOnNavigate: true,
      }}
    >
      <AuthProvider>
        <Router>
          <RouteScrollReset />
          <Routes>
            {/* Everything except /admin is the one-page site */}
            <Route path="/admin/*" element={<AdminPages />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ReactLenis>
  );
}

export default App;